import * as React from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import { MapPin, Crosshair } from 'lucide-react';
import CloseIcon from '@mui/icons-material/Close';

/**
 * MapPickerModal
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onConfirm: (loc: { title: string, address?: string, lat: number, lng: number, url?: string, staticSrc?: string }) => void
 *  - gKey?: string (Google Static Maps API key)
 *  - mbx?: string (Mapbox access token)
 *  - initial?: { title?: string, address?: string, lat?: number, lng?: number }
 */

function clamp(n, min, max) { return Math.min(max, Math.max(min, n)); }

function isValidLat(n) { return Number.isFinite(n) && n >= -90 && n <= 90; }
function isValidLng(n) { return Number.isFinite(n) && n >= -180 && n <= 180; }

function buildGoogleStatic({ lat, lng, zoom = 16, w = 600, h = 300, title, gKey }) {
  if (!gKey) return null;
  const center = `${lat},${lng}`;
  const marker = encodeURIComponent(`color:red|label:${(title || '·').slice(0,1)}|${center}`);
  return `https://maps.googleapis.com/maps/api/staticmap?center=${center}&zoom=${zoom}&size=${w}x${h}&maptype=roadmap&markers=${marker}&key=${gKey}`;
}

function buildMapboxStatic({ lat, lng, zoom = 16, w = 600, h = 300, title, mbx }) {
  if (!mbx) return null;
  const pin = encodeURIComponent(`pin-l+e91e63(${lng},${lat})`);
  return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${pin}/${lng},${lat},${zoom}/${w}x${h}?access_token=${mbx}`;
}

// --- Google Maps loader ---
function loadGoogleMaps(apiKey) {
  if (!apiKey) return Promise.reject(new Error('No Google API key'));
  const w = window;
  if (w.__gmapsPromise) return w.__gmapsPromise;

  const waitForReady = () => new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      if (w.google && w.google.maps) return resolve(w.google.maps);
      if (Date.now() - started > 15000) return reject(new Error('Google Maps JS load timeout'));
      setTimeout(tick, 50);
    };
    tick();
  });

  w.__gmapsPromise = new Promise((resolve, reject) => {
    if (w.google && w.google.maps) return resolve(w.google.maps);

    const scriptId = 'ef-gmaps-js';
    let script = document.getElementById(scriptId);
    if (script) {
      // Script tag exists but may still be loading; wait for readiness.
      waitForReady().then(resolve).catch((e) => { console.error('[MapPickerModal] Google Maps wait failed', e); reject(e); });
      return;
    }

    script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => waitForReady().then(resolve).catch((e) => { console.error('[MapPickerModal] Google Maps wait failed after onload', e); reject(e); });
    script.onerror = (e) => { console.error('[MapPickerModal] Google Maps JS failed to load', e); reject(e); };
    document.head.appendChild(script);
  });
  return w.__gmapsPromise;
}

function looksLikePlusCodeAddress(s) {
  return !!s && /\b[A-Z0-9]{4,}\+[A-Z0-9]{2,}\b/.test(String(s));
}

async function fetchPlaceFormattedAddress(gmaps, placeId) {
  return new Promise((resolve) => {
    try {
      const svc = new gmaps.places.PlacesService(document.createElement('div'));
      svc.getDetails({ placeId, fields: ['formatted_address','vicinity','name','address_components'] }, (res, status) => {
        if (status === gmaps.places.PlacesServiceStatus.OK && res) {
          try { console.log('[MapPickerModal][placeDetails]', res); } catch {}
          resolve({
            formatted: res.formatted_address || null,
            vicinity: res.vicinity || null,
            components: res.address_components || [],
          });
        } else {
          resolve(null);
        }
      });
    } catch (e) { resolve(null); }
  });
}

// Helper: Geocode by placeId (for locales where Place Details omits formatted_address)
async function geocodePlaceId(gmaps, placeId) {
  return new Promise((resolve) => {
    try {
      const geocoder = new gmaps.Geocoder();
      geocoder.geocode({ placeId }, (results, status) => {
        try { console.log('[MapPickerModal][geocodePlaceId]', status, results && results[0]); } catch {}
        if (status === 'OK' && results && results.length) {
          resolve(results[0].formatted_address || null);
        } else {
          resolve(null);
        }
      });
    } catch (e) { resolve(null); }
  });
}


// Places Text Search helper: finds a placeId (and optionally formatted address) for a query near coords
async function textSearchForAddress(gmaps, name, lat, lng) {
  return new Promise((resolve) => {
    try {
      const svc = new gmaps.places.PlacesService(document.createElement('div'));
      const req = { query: `${name} Krong Siem Reap`, location: new gmaps.LatLng(lat, lng), radius: 800 };
      svc.textSearch(req, async (results, status) => {
        try { console.log('[MapPickerModal][textSearch]', status, results && results[0]); } catch {}
        if (status === gmaps.places.PlacesServiceStatus.OK && results && results.length) {
          const p = results[0];
          resolve({ placeId: p.place_id || null });
        } else {
          resolve(null);
        }
      });
    } catch (e) { resolve(null); }
  });
}

// Helper: Build a richer street/vicinity address from Google address_components
function buildAddressFromComponents(components) {
  if (!Array.isArray(components) || !components.length) return '';
  const get = (type) => {
    const c = components.find((x) => (x.types || []).includes(type));
    return c ? (c.long_name || c.short_name || '') : '';
  };

  const streetNumber = get('street_number');
  const route = get('route');
  const premise = get('premise') || get('point_of_interest') || get('establishment');
  const sublocality = get('sublocality') || get('sublocality_level_1');
  const locality = get('locality');
  const admin2 = get('administrative_area_level_2');
  const admin1 = get('administrative_area_level_1');
  const country = get('country');

  // Build a line for street if present, but we generally prefer area/sublocality first in chat UI
  const streetLine = route ? [streetNumber, route].filter(Boolean).join(' ') : '';

  // Prefer sublocality (e.g., "Sala Kamreuk"), then locality ("Siem Reap"), then province and country.
  // Only include admin2 when locality is missing (admin2 often duplicates locality names).
  const seq = [
    sublocality || streetLine || premise,
    locality || (locality ? '' : admin2),
    admin1,
    country,
  ];

  // Deduplicate adjacent or equal parts, case-insensitive, and drop empties
  const parts = [];
  for (const raw of seq) {
    const part = (raw || '').trim();
    if (!part) continue;
    const prev = parts[parts.length - 1];
    if (!prev || prev.toLowerCase() !== part.toLowerCase()) {
      parts.push(part);
    }
  }

  // If we still have nothing, fall back to whatever we can collect sensibly
  if (!parts.length) {
    const fallback = [streetLine || premise, sublocality, locality || admin2, admin1, country]
      .filter(Boolean)
      .reduce((acc, v) => {
        if (!acc.length || acc[acc.length - 1].toLowerCase() !== v.toLowerCase()) acc.push(v);
        return acc;
      }, []);
    return fallback.join(', ');
  }

  return parts.join(', ');
}

function MapCanvas({ gKey, value, onChange, zoom, onZoomChange }) {
  const ref = React.useRef(null);
  const mapRef = React.useRef(null);
  const markerRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!gKey) return; // no map without key (fallback handled by parent)
    loadGoogleMaps(gKey).then((gmaps) => {
      if (cancelled) return;
      const center = { lat: value.lat, lng: value.lng };
      const map = new gmaps.Map(ref.current, {
        center,
        zoom: zoom ?? 16,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      mapRef.current = map;
      const marker = new gmaps.Marker({ position: center, map, draggable: true });
      markerRef.current = marker;
      const emit = (latLng) => {
        const lat = +latLng.lat().toFixed(6);
        const lng = +latLng.lng().toFixed(6);
        onChange && onChange({ lat, lng });
      };
      map.addListener('click', (e) => emit(e.latLng));
      marker.addListener('dragend', (e) => emit(e.latLng));
      map.addListener('zoom_changed', () => onZoomChange && onZoomChange(map.getZoom()));
      try { console.debug('[MapPickerModal] Map init ok'); } catch {}
    }).catch((e) => {
      try { console.error('[MapPickerModal] Map init failed', e); } catch {}
    });
    return () => { cancelled = true; };
  }, [gKey, onChange, onZoomChange, value.lat, value.lng, zoom]);

  // Keep marker/map in sync when inputs change
  React.useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    const pos = new window.google.maps.LatLng(value.lat, value.lng);
    marker.setPosition(pos);
    map.setCenter(pos);
  }, [value.lat, value.lng]);

  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || zoom == null) return;
    map.setZoom(zoom);
  }, [zoom]);

  return (
    <Box
      ref={ref}
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: 0,
        overflow: 'hidden',
        border: 0,
        boxShadow: 'none',
      }}
    />
  );
}

export default function MapPickerModal({ open, onClose, onConfirm, gKey, mbx, initial }) {
  const [title, setTitle] = React.useState(initial?.title || '');
  const [address, setAddress] = React.useState(initial?.address || '');
  const [lat, setLat] = React.useState(
    typeof initial?.lat === 'number' ? clamp(initial.lat, -90, 90) : ''
  );
  const [lng, setLng] = React.useState(
    typeof initial?.lng === 'number' ? clamp(initial.lng, -180, 180) : ''
  );
  const [zoom, setZoom] = React.useState(16);
  const [loadingGeo, setLoadingGeo] = React.useState(false);
  const [geoError, setGeoError] = React.useState(null);


  // Resolve Google Maps key (must be defined before any callbacks that reference it)
  const effectiveGKey = React.useMemo(() => {
    // 1) explicit prop
    if (gKey && typeof gKey === 'string' && gKey.trim()) return gKey.trim();

    // 2) CRA env (baked at build)
    if (typeof process !== 'undefined' && process.env) {
      const k = process.env.REACT_APP_GOOGLE_MAPS_KEY || process.env.REACT_APP_GOOGLE_STATIC_MAPS_KEY;
      if (k) return k.trim();
    }

    // 3) Vite env
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      const k = import.meta.env.VITE_GOOGLE_MAPS_KEY || import.meta.env.VITE_GOOGLE_STATIC_KEY || import.meta.env.VITE_GMAPS_KEY;
      if (k) return String(k).trim();
    }

    // 4) Window globals used across the app
    if (typeof window !== 'undefined') {
      const w = window;
      const cand = (
        w.__GOOGLE_MAPS_KEY__ ||
        w.__GOOGLE_STATIC_MAPS_KEY ||
        w.__GMAPS_KEY__ ||
        (w.__MAP_KEYS__ && (w.__MAP_KEYS__.google || w.__MAP_KEYS__.googleKey)) ||
        (w.__APP_CONFIG__ && (w.__APP_CONFIG__.googleMapsKey || w.__APP_CONFIG__.googleStaticKey))
      );
      if (cand) return String(cand).trim();
    }

    // 5) Meta tags (runtime injection without rebuild)
    if (typeof document !== 'undefined') {
      const metaNames = ['google-maps-key', 'google-static-key', 'gmap-key'];
      for (const name of metaNames) {
        const el = document.querySelector(`meta[name="${name}"]`);
        const content = el && el.getAttribute('content');
        if (content) return content.trim();
      }
    }

    // 6) LocalStorage fallbacks (various casings)
    if (typeof localStorage !== 'undefined') {
      const keys = [
        'googleMapsKey', 'googleStaticKey',
        'GOOGLE_MAPS_KEY', 'GOOGLE_STATIC_MAPS_KEY',
        'google_maps_key', 'google_static_maps_key',
      ];
      for (const k of keys) {
        const v = localStorage.getItem(k);
        if (v) return v.trim();
      }
    }

    return null;
  }, [gKey]);

  // Search box refs
  const searchInputRef = React.useRef(null);
  const autocompleteRef = React.useRef(null);
  // When user picks from autocomplete, remember the chosen place
  const selectedPlaceRef = React.useRef(null); // { placeId, name, address, components, lat, lng, ts }
  const suppressGeocodeRef = React.useRef(false);
  // Search query state for search box
  const [searchQuery, setSearchQuery] = React.useState('');

  // Default center and derived view values (must be defined before any effects use them)
  const defaultCenter = { lat: 13.736717, lng: 100.523186 }; // neutral default (Bangkok)
  const viewLat = isValidLat(Number(lat)) ? Number(lat) : defaultCenter.lat;
  const viewLng = isValidLng(Number(lng)) ? Number(lng) : defaultCenter.lng;
  const canPreview = isValidLat(Number(lat)) && isValidLng(Number(lng));

  // Ensure Google Places dropdown floats above MUI Dialog and map canvas
  React.useEffect(() => {
    const id = 'ef-google-pac-zindex';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      .pac-container { z-index: 2000 !important; }
      .pac-container:empty { display: none; }
    `;
    document.head.appendChild(style);
    return () => { try { document.getElementById(id)?.remove(); } catch {} };
  }, []);
  // Init Places Autocomplete on the search input when modal opens
  React.useEffect(() => {
    if (!open || !effectiveGKey) return;
    let cleanup = () => {};
    (async () => {
      try {
        const gmaps = await loadGoogleMaps(effectiveGKey);
        if (!gmaps?.places || !searchInputRef.current) return;
        const ac = new gmaps.places.Autocomplete(searchInputRef.current, {
          fields: ['place_id','geometry','name','formatted_address','address_components'],
          types: [],
        });
        // Bias autocomplete to the current map center/viewport so closest results appear first
        try {
          const sw = new gmaps.LatLng(Math.max(-90, viewLat - 0.25), Math.max(-180, viewLng - 0.25));
          const ne = new gmaps.LatLng(Math.min(90, viewLat + 0.25), Math.min(180, viewLng + 0.25));
          const bounds = new gmaps.LatLngBounds(sw, ne);
          ac.setBounds(bounds);
          // Modern API: locationBias (if available)
          if (ac.setOptions) {
            ac.setOptions({
              bounds,
              strictBounds: false,
              // Some versions support locationBias; wrap in try so it won't throw
              locationBias: { center: new gmaps.LatLng(viewLat, viewLng), radius: 5000 },
            });
          }
        } catch {}
        autocompleteRef.current = ac;
        const listener = ac.addListener('place_changed', async () => {
          const place = ac.getPlace();
          try { console.log('[MapPickerModal][autocomplete] place', place); } catch {}
          if (place && place.geometry && place.geometry.location) {
            const loc = place.geometry.location;
            const nlat = +loc.lat().toFixed(6);
            const nlng = +loc.lng().toFixed(6);
            setLat(nlat);
            setLng(nlng);
            setZoom(17);
            const name = place.name || 'Location';
            let addr = place.formatted_address || '';
            if ((!addr || addr.split(',').length < 2) && place.address_components) {
              const built = buildAddressFromComponents(place.address_components);
              if (built) addr = built;
            }
            setTitle(name);
            setAddress(addr);
            selectedPlaceRef.current = { placeId: place.place_id || null, name, address: addr, components: place.address_components || [], lat: nlat, lng: nlng, ts: Date.now() };
            suppressGeocodeRef.current = true;
            setTimeout(() => { suppressGeocodeRef.current = false; }, 1000);
          } else if (place && place.place_id) {
            try {
              const svc = new gmaps.places.PlacesService(document.createElement('div'));
              svc.getDetails({ placeId: place.place_id, fields: ['geometry','name','formatted_address','address_components'] }, (res, status) => {
                if (status === gmaps.places.PlacesServiceStatus.OK && res && res.geometry && res.geometry.location) {
                  const loc = res.geometry.location;
                  const nlat = +loc.lat().toFixed(6);
                  const nlng = +loc.lng().toFixed(6);
                  setLat(nlat);
                  setLng(nlng);
                  setZoom(17);
                  const name = res.name || 'Location';
                  let addr = res.formatted_address || '';
                  if ((!addr || addr.split(',').length < 2) && res.address_components) {
                    const built = buildAddressFromComponents(res.address_components);
                    if (built) addr = built;
                  }
                  setTitle(name);
                  setAddress(addr);
                  selectedPlaceRef.current = { placeId: res.place_id || place.place_id || null, name, address: addr, components: res.address_components || [], lat: nlat, lng: nlng, ts: Date.now() };
                  suppressGeocodeRef.current = true;
                  setTimeout(() => { suppressGeocodeRef.current = false; }, 1000);
                }
              });
            } catch {}
          }
        });
        cleanup = () => listener && listener.remove && listener.remove();
      } catch {}
    })();
    return () => cleanup();
  }, [open, effectiveGKey, viewLat, viewLng]);

  // Keep Autocomplete biased to the current view center
  React.useEffect(() => {
    const ac = autocompleteRef.current;
    if (!ac || !effectiveGKey) return;
    (async () => {
      try {
        const gmaps = await loadGoogleMaps(effectiveGKey);
        const vlat = isValidLat(Number(viewLat)) ? Number(viewLat) : 0;
        const vlng = isValidLng(Number(viewLng)) ? Number(viewLng) : 0;
        const sw = new gmaps.LatLng(Math.max(-90, vlat - 0.25), Math.max(-180, vlng - 0.25));
        const ne = new gmaps.LatLng(Math.min(90, vlat + 0.25), Math.min(180, vlng + 0.25));
        const bounds = new gmaps.LatLngBounds(sw, ne);
        if (ac.setBounds) ac.setBounds(bounds);
        if (ac.setOptions) ac.setOptions({ bounds, strictBounds: false, locationBias: { center: new gmaps.LatLng(vlat, vlng), radius: 5000 } });
      } catch {}
    })();
  }, [viewLat, viewLng, effectiveGKey]);

  // Refs for geocoder and debounce timer
  const geocoderRef = React.useRef(null);
  const geocodeTimerRef = React.useRef(null);

  // Auto-geolocate once per open when no lat/lng provided
  const autoGeoTriedRef = React.useRef(false);

  // Helper to reverse geocode using Google Maps Geocoder
  const reverseGeocode = React.useCallback(async (latNum, lngNum) => {
    if (!effectiveGKey) return null;
    try {
      const gmaps = await loadGoogleMaps(effectiveGKey);
      if (!geocoderRef.current) geocoderRef.current = new gmaps.Geocoder();
      const gc = geocoderRef.current;
      return new Promise((resolve) => {
        gc.geocode({ location: { lat: latNum, lng: lngNum } }, (results, status) => {
          try { console.log('[MapPickerModal][reverseGeocode] status', status, results); } catch {}
          if (status !== 'OK' || !results || !results.length) return resolve(null);
          const prefer = results.find(r => (r.types||[]).some(t => (
            t === 'point_of_interest' || t === 'establishment' || t === 'premise' || t === 'natural_feature' || t === 'tourist_attraction'
          )));
          const best = prefer || results[0];
          const formatted = best.formatted_address || results[0].formatted_address;
          let name = null;
          const components = best.address_components || results[0].address_components || [];
          const builtAddress = buildAddressFromComponents(components);
          const chosenAddress = builtAddress && builtAddress.length > (formatted ? formatted.length : 0)
            ? builtAddress
            : (builtAddress && !formatted ? builtAddress : formatted);
          if (best && best.address_components && best.address_components.length) {
            const first = best.address_components[0];
            name = first.long_name || first.short_name || null;
          }
          if (!name && formatted) name = formatted.split(',')[0].trim();
          try { console.log('[MapPickerModal][reverseGeocode] got results', { name, formatted, builtAddress, best }); } catch {}
          resolve({ title: name || 'Location', address: chosenAddress || formatted, components });
        });
      });
    } catch (e) {
      return null;
    }
  }, [effectiveGKey]);

  // Try to find a nearby POI/establishment name using Places API (distance-ranked, fallback to radius)
  const lookupNearbyPlaceName = React.useCallback(async (latNum, lngNum) => {
    if (!effectiveGKey) return null;
    try {
      const gmaps = await loadGoogleMaps(effectiveGKey);
      if (!gmaps.places) { console.warn('[MapPickerModal] places library missing'); return null; }
      // PlacesService requires a Map or HTMLDivElement; a detached div is fine
      return new Promise((resolve) => {
        const svc = new gmaps.places.PlacesService(document.createElement('div'));
        const loc = new gmaps.LatLng(latNum, lngNum);
        // Pass 1: distance-ranked search for lodging
        svc.nearbySearch({ location: loc, rankBy: gmaps.places.RankBy.DISTANCE, type: 'lodging' }, (results, status) => {
          try { console.log('[MapPickerModal][lookupNearbyPlaceName] pass1', status, results); } catch {}
          const ok = status === gmaps.places.PlacesServiceStatus.OK && results && results.length;
          const pick = ok ? results.find(r => r && r.name) : null;
          if (pick) return resolve({ name: pick.name, placeId: pick.place_id || null, vicinity: pick.vicinity || null });
          // Pass 2: radius fallback (establishment, 400m)
          svc.nearbySearch({ location: loc, radius: 400, type: 'establishment' }, (r2, s2) => {
            try { console.log('[MapPickerModal][lookupNearbyPlaceName] pass2', s2, r2); } catch {}
            const ok2 = s2 === gmaps.places.PlacesServiceStatus.OK && r2 && r2.length;
            const best = ok2 ? r2[0] : null;
            resolve(best ? { name: best.name, placeId: best.place_id || null, vicinity: best.vicinity || null } : null);
          });
        });
      });
    } catch (e) {
      return null;
    }
  }, [effectiveGKey]);

  React.useEffect(() => {
    try { console.log('[MapPickerModal] effectiveGKey', { present: !!effectiveGKey, head: effectiveGKey ? String(effectiveGKey).slice(0,8) + '…' : null }); } catch {}
  }, [effectiveGKey]);


  React.useEffect(() => {
    try { console.debug('[MapPickerModal] open', { open, hasGKey: !!effectiveGKey }); } catch {}
  }, [open, effectiveGKey]);

  React.useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || '');
    setAddress(initial?.address || '');
    setLat(typeof initial?.lat === 'number' ? clamp(initial.lat, -90, 90) : '');
    setLng(typeof initial?.lng === 'number' ? clamp(initial.lng, -180, 180) : '');
    setZoom(16);
  }, [open, initial?.title, initial?.address, initial?.lat, initial?.lng]);

  const requestGeolocation = React.useCallback((why = 'auto') => {
    if (!('geolocation' in navigator)) {
      setGeoError('Geolocation not available');
      return;
    }
    setGeoError(null);
    setLoadingGeo(true);

    const onSuccess = (pos) => {
      setLoadingGeo(false);
      const nlat = +pos.coords.latitude.toFixed(6);
      const nlng = +pos.coords.longitude.toFixed(6);
      setLat(nlat);
      setLng(nlng);
      setZoom((z) => (z < 14 ? 16 : z));
    };
    const onError = (err) => {
      setLoadingGeo(false);
      const msg = err && err.code === 1
        ? 'Location permission denied'
        : err && err.code === 2
          ? 'Position unavailable'
          : err && err.code === 3
            ? 'Location request timed out'
            : 'Failed to get location';
      setGeoError(msg);
    };

    try {
      const primaryOpts = { enableHighAccuracy: false, timeout: 20000, maximumAge: 300000 };
      const fallbackOpts = { enableHighAccuracy: false, timeout: 30000, maximumAge: 0 };

      const attempt = (opts, retry = false) => {
        navigator.geolocation.getCurrentPosition(onSuccess, (err) => {
          if (!retry && (err?.code === 2 || err?.code === 3)) {
            console.warn('[MapPickerModal][geolocation] retrying with fallback opts', err);
            setLoadingGeo(true);
            attempt(fallbackOpts, true);
          } else {
            onError(err);
          }
        }, opts);
      };

      if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: 'geolocation' }).then((p) => {
          if (p.state === 'denied') {
            setLoadingGeo(false);
            setGeoError('Location permission denied');
            return;
          }
          attempt(primaryOpts);
        }).catch(() => {
          attempt(primaryOpts);
        });
      } else {
        attempt(primaryOpts);
      }
    } catch (e) {
      setLoadingGeo(false);
      setGeoError('Failed to get location');
    }
  }, [setLat, setLng, setZoom]);

  React.useEffect(() => {
    if (!open) { autoGeoTriedRef.current = false; return; }
    if (autoGeoTriedRef.current) return; // run once per open
    autoGeoTriedRef.current = true;
    // defer slightly so the modal paints before the permission prompt
    const t = setTimeout(() => requestGeolocation('auto'), 150);
    return () => clearTimeout(t);
  }, [open, requestGeolocation]);

  const handleUseMyLocation = () => requestGeolocation('click');


  // Only used when no interactive map is available
  const googleSrc = canPreview ? buildGoogleStatic({ lat: Number(lat), lng: Number(lng), zoom, w: 640, h: 320, title, gKey: effectiveGKey }) : null;
  const mapboxSrc = canPreview ? buildMapboxStatic({ lat: Number(lat), lng: Number(lng), zoom, w: 640, h: 320, title, mbx }) : null;
  const staticSrc = googleSrc || mapboxSrc || null;

  const handleConfirm = async () => {
    const nlat = Number(lat);
    const nlng = Number(lng);

    try { 
      console.log('[MapPickerModal][handleConfirm] coords', { nlat, nlng, title, address }); 
    } catch {}
    if (!isValidLat(nlat) || !isValidLng(nlng)) return;

    // If user selected from autocomplete and the marker hasn’t moved far, trust that selection
    const sel = selectedPlaceRef.current;
    const nearSelection = sel && Math.hypot((sel.lat - nlat) * 111000, (sel.lng - nlng) * 111000 * Math.cos((nlat * Math.PI)/180)) <= 60;

    // Build URLs for preview/link
    const staticSrcCandidate = isValidLat(nlat) && isValidLng(nlng)
      ? (buildGoogleStatic({ lat: nlat, lng: nlng, zoom, w: 640, h: 320, title, gKey: effectiveGKey })
         || buildMapboxStatic({ lat: nlat, lng: nlng, zoom, w: 640, h: 320, title, mbx }))
      : null;

    const mapsUrl = (effectiveGKey)
      ? `https://maps.google.com/?q=${nlat},${nlng}`
      : `https://www.openstreetmap.org/?mlat=${nlat}&mlon=${nlng}#map=16/${nlat}/${nlng}`;

    // Prefer the user’s selection on confirm
    let finalTitle = (nearSelection && sel?.name) ? sel.name : ((title && title.trim()) || '');
    let finalAddress = (nearSelection && (sel?.address || (sel?.components && buildAddressFromComponents(sel.components)))) ? (sel.address || buildAddressFromComponents(sel.components)) : ((address && address.trim()) || '');

    const looksWeak = !finalTitle || finalTitle === 'Location' || /\b[A-Z0-9]{4,}\+[A-Z0-9]{2,}\b/.test(finalTitle);

    if (effectiveGKey) {
      try {
        // Baseline reverse geocode (may return a Plus Code address)
        const info = await reverseGeocode(nlat, nlng);
        if (info) {
          if (!finalTitle || looksWeak) finalTitle = info.title || finalTitle;
          if (!finalAddress) finalAddress = info.address || finalAddress;
        }
        if (info && (!finalAddress || finalAddress.indexOf(',') === -1 || finalAddress.split(',').length < 2 || finalAddress.trim().length <= 16)) {
          const built = buildAddressFromComponents(info.components || []);
          if (built) finalAddress = built;
        }
        // If title is weak OR address looks like a Plus Code, query nearest POI
        let poi = null;
        if (!nearSelection && (looksWeak || looksLikePlusCodeAddress(finalAddress))) {
          poi = await lookupNearbyPlaceName(nlat, nlng);
          if (poi && poi.name) finalTitle = poi.name;
        }
        // If we still have a Plus Code/empty address, fetch Place Details for a formatted address or via Geocoder fallback
        if ((!finalAddress || looksLikePlusCodeAddress(finalAddress)) && poi && poi.placeId) {
          const gmaps = await loadGoogleMaps(effectiveGKey);
          if (gmaps?.places) {
            const details = await fetchPlaceFormattedAddress(gmaps, poi.placeId);
            if (details) {
              const built = buildAddressFromComponents(details.components || []);
              finalAddress = built || details.formatted || details.vicinity || finalAddress;
            } else {
              // Some locales omit formatted_address in Details; try Geocoder by placeId
              const addr = await geocodePlaceId(gmaps, poi.placeId);
              if (addr) finalAddress = addr;
            }
          }
          if ((!finalAddress || looksLikePlusCodeAddress(finalAddress)) && poi && poi.vicinity) {
            finalAddress = poi.vicinity;
          }
        }
      } catch (_) {
        // ignore; fall back to existing title/address
      }
    }

    // Final fallback: if address is missing or still too generic (no commas, single segment, very short, or Plus Code),
    // try a text search by title near the coords and then fetch Place Details for a full formatted address.
    {
      const tooGeneric = !finalAddress
        || looksLikePlusCodeAddress(finalAddress)
        || finalAddress.indexOf(',') === -1
        || finalAddress.split(',').length < 2
        || finalAddress.trim().length <= 16;
      if (!nearSelection && tooGeneric && finalTitle && effectiveGKey) {
        try {
          const gmaps = await loadGoogleMaps(effectiveGKey);
          if (gmaps?.places) {
            const hit = await textSearchForAddress(gmaps, finalTitle, nlat, nlng);
            if (hit?.placeId) {
              const details = await fetchPlaceFormattedAddress(gmaps, hit.placeId);
              if (details) {
                const built = buildAddressFromComponents(details.components || []);
                finalAddress = built || details.formatted || details.vicinity || finalAddress;
              } else {
                const addr = await geocodePlaceId(gmaps, hit.placeId);
                if (addr) finalAddress = addr;
              }
            }
          }
        } catch {}
      }
    }

    if (!finalTitle) finalTitle = 'Location';

    {
      const plusCodeRe = /\b[A-Z0-9]{4,}\+[A-Z0-9]{2,}\b/;
      if (plusCodeRe.test(finalAddress)) {
        try { console.log('[MapPickerModal] suppressing plus-code address', finalAddress); } catch {}
        finalAddress = '';
      }
    }

    onConfirm?.({
      title: finalTitle,
      address: finalAddress || undefined,
      lat: nlat,
      lng: nlng,
      url: mapsUrl,
      staticSrc: staticSrcCandidate || undefined,
    });
    try { 
      console.log('[MapPickerModal][handleConfirm] final result', { finalTitle, finalAddress, mapsUrl, staticSrcCandidate }); 
    } catch {}
    onClose?.();
  };

  // Debounced reverse geocoding effect when lat/lng change and are valid
  React.useEffect(() => {
    if (!effectiveGKey) return;
    if (suppressGeocodeRef.current) return;
    const nlat = Number(lat); const nlng = Number(lng);
    if (!isValidLat(nlat) || !isValidLng(nlng)) return;
    if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current);
    geocodeTimerRef.current = setTimeout(async () => {
      // First, reverse geocode for address
      const info = await reverseGeocode(nlat, nlng);
      let nextTitle = info?.title || 'Location';
      let nextAddress = info?.address || '';
      if (!nextAddress || nextAddress.indexOf(',') === -1 || nextAddress.split(',').length < 2 || nextAddress.trim().length <= 16) {
        const built = buildAddressFromComponents(info?.components || []);
        if (built) nextAddress = built;
      }
      // If the title or address looks like a Plus Code (e.g., ABCD+XYZ), try Places nearby for a real name/address
      const looksLikePlusCode = /\b[A-Z0-9]{4,}\+[A-Z0-9]{2,}\b/.test(String(nextTitle));
      if (looksLikePlusCode || !nextTitle || nextTitle === 'Location' || looksLikePlusCodeAddress(nextAddress)) {
        const poi = await lookupNearbyPlaceName(nlat, nlng);
        if (poi?.name) nextTitle = poi.name;
        if (looksLikePlusCodeAddress(nextAddress) && poi?.vicinity) nextAddress = poi.vicinity;
      }
      setTitle(nextTitle);
      setAddress(nextAddress);
    }, 400);
    return () => { if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current); };
  }, [lat, lng, effectiveGKey, reverseGeocode, lookupNearbyPlaceName]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      slotProps={{
        backdrop: { sx: { backdropFilter: 'blur(2px)' } },
      }}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(255,255,255,0.20)',
          borderRadius: 3,
          boxShadow: '0 12px 36px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent dividers={false} sx={{ p: 0 }}>
        <Box sx={{ position: 'relative', width: '100%', height: 'min(78vh, 820px)' }}>
          {/* Map canvas */}
          {effectiveGKey ? (
            <>
              <Box sx={{ position: 'absolute', inset: 0 }}>
                <MapCanvas
                  gKey={effectiveGKey}
                  value={{ lat: viewLat, lng: viewLng }}
                  zoom={zoom}
                  onZoomChange={(z) => setZoom(z)}
                  onChange={({ lat: nlat, lng: nlng }) => {
                    setLat(nlat);
                    setLng(nlng);
                    const sel = selectedPlaceRef.current;
                    if (sel) {
                      const movedFar = Math.hypot((sel.lat - nlat) * 111000, (sel.lng - nlng) * 111000 * Math.cos((nlat * Math.PI)/180)) > 120;
                      if (movedFar) selectedPlaceRef.current = null;
                    }
                  }}
                />
              </Box>

              {/* Top gradient for controls contrast */}
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 56, background: 'linear-gradient(180deg, rgba(255,255,255,0.55), rgba(255,255,255,0))', pointerEvents: 'none' }} />

              {/* Close (top-right) */}
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  position: 'absolute',
                  right: 10,
                  top: 10,
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: 1,
                  zIndex: 1700,
                  width: 32,
                  height: 32,
                  p: 0,
                  borderRadius: '50%',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              {/* Places search (top-centered, compact) */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 'min(560px, calc(100% - 140px))',
                  pointerEvents: 'none',
                  zIndex: 1500,
                  display: 'flex',
                }}
              >
                <TextField
                  inputRef={searchInputRef}
                  placeholder="Search places"
                  size="small"
                  fullWidth
                  variant="outlined"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => { /* allow focus & input */ }}
                  InputProps={{
                    sx: {
                      pointerEvents: 'auto',
                      backgroundColor: 'rgba(255,255,255,0.85)',
                      borderRadius: 5,
                      px: 1.25,
                      py: 0.25,
                      fontSize: '0.875rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent', borderWidth: 1 },
                    },
                    endAdornment: searchQuery ? (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchQuery('');
                          if (searchInputRef.current) searchInputRef.current.value = '';
                        }}
                        sx={{ mr: -1 }}
                      >
                        ✕
                      </IconButton>
                    ) : null,
                  }}
                />
              </Box>

              {/* Geolocate (top-left) */}
              <Tooltip
                title={loadingGeo ? 'Locating…' : 'Use my location'}
                disableInteractive
                slotProps={{ tooltip: { sx: { pointerEvents: 'none' } } }}
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={handleUseMyLocation}
                    disabled={loadingGeo}
                    sx={{ position: 'absolute', top: 12, left: 12, zIndex: 1600, backdropFilter: 'blur(8px)', background: 'rgba(255,255,255,0.7)', borderRadius: '50%', boxShadow: 2, '&:hover': { background: 'rgba(255,255,255,0.85)' } }}
                  >
                    <Crosshair size={18} />
                  </IconButton>
                </span>
              </Tooltip>

              {/* Error/status (top-right below close) */}
              {(loadingGeo || geoError) && (
                <Box sx={{ position: 'absolute', top: 56, right: 12, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 1, px: 1, py: 0.5, boxShadow: 1 }}>
                  <Typography variant="caption" sx={{ color: geoError ? '#b91c1c' : '#374151' }}>
                    {loadingGeo ? 'Locating…' : geoError}
                  </Typography>
                </Box>
              )}

              {/* Confirm (bottom-right) */}
              <Box sx={{ position: 'absolute', right: 12, bottom: 12 }}>
                <Button
                  variant="contained"
                  onClick={handleConfirm}
                  disabled={!canPreview}
                  sx={{ borderRadius: '999px', textTransform: 'none', px: 2.25, py: 0.75 }}
                >
                  Add location
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', p: 3 }}>
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                Interactive map requires a Google Maps key (gKey).
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
